import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Search, Loader2, ChevronRight } from 'lucide-react';

interface AdminUser {
  userid: string;
  email: string;
  userhandle: string;
  user_type: string;
  sub_status: string;
  sub_type: string;
  sub_end: string;
  sub_amt: number;
  createdAt: string;
  Org_ID?: string;
}

const USER_TYPE_COLORS: Record<string, string> = {
  snappy: 'bg-purple-100 text-purple-800',
  admin: 'bg-red-100 text-red-800',
  user: 'bg-green-100 text-green-800',
  trial: 'bg-yellow-100 text-yellow-800',
  test: 'bg-gray-100 text-gray-800',
};

export function UserManagement() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const loadUsers = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const data = await api.listUsers(searchTerm, 100);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSearch = () => {
    loadUsers(search);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">User Management</h1>

      {/* Search */}
      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search by email, username, or user ID..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Search
        </button>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500 mb-4">
        Showing {users.length} of {total} users
      </div>

      {/* User Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users found</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Handle</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-gray-500 uppercase">Sub Status</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr
                  key={user.userid}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/f47ac10b58cc/users/${user.userid}`)}
                >
                  <td className="py-3 px-4 text-sm">{user.email}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{user.userhandle || '-'}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      USER_TYPE_COLORS[String(user.user_type).toLowerCase()] || 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.user_type || 'unknown'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      String(user.sub_status).toLowerCase() === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {user.sub_status || '-'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-sm">
                    {user.sub_amt ? `$${Number(user.sub_amt).toFixed(2)}` : '-'}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="py-3 px-4">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
